import type {
	LandUseChangeResponse,
	ModuleFull,
} from "@features/modules/landUseChanges/types";
import {
	landUseChangeResponseSchema
} from "@features/modules/landUseChanges/types";
import { FEModules, FEModulesObj } from "@utils/moduleList";
import { apiSlice, publicApiSlice, TagTypes } from "../../apiSlice";
import { createEntriesGetApi, createModuleGetApi, createModuleUpdateApi } from "./factories/createModuleApi";
import { transformModuleResponse, prepareModulePayload } from "./factories/moduleApiUtils";

const includedPropList = [
	"id",
	"start_year",
	"soc_t2_start",
	"soc_t2_w",
	"soc_t2_wo",
	"area",
	"note",
	"is_fire_used_start",
	"is_fire_used_w",
	"is_fire_used_wo",
	"dry_matter_w",
	"dry_matter_wo",
	"module_type_start",
	"module_type_w",
	"module_type_wo",
	"module_type_thread",
	"is_fire_used_thread",
	"dry_matter_thread",
];

// Define constants for the module
const MODULE_TYPE = "LandUseChange";
const TAG: TagTypes = "LandUseChange";
const ENDPOINT = FEModulesObj[FEModules.LandUseChange].path;

const transformResponse = (response: unknown) => {
	const parsed = landUseChangeResponseSchema.safeParse(response);
	if (!parsed.success) {
		throw new Error(
			`Land use change API response validation failed: ${parsed.error.message}`
		);
	}
	return transformModuleResponse<LandUseChangeResponse, ModuleFull>({
		response: parsed.data as LandUseChangeResponse,
		includedPropList,
	});
};

const landUseChangeApiSlice = createModuleGetApi<ModuleFull>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	transformResponse,
})

const publicLandUseChangeApiSlice = createModuleGetApi<ModuleFull>({
	apiSlice: publicApiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	transformResponse,
})

const transformUpdatePayload = (data: Partial<ModuleFull>, activityId: number) => prepareModulePayload({
	moduleData: data.module,
	tiertwoData: {}, // Land use change doesn't have tier two data
	activityId
})

const mutationApiSlice = createModuleUpdateApi<ModuleFull, LandUseChangeResponse>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	transformUpdatePayload,
	includesLandUseChange: true
})

export const {
	useGetLandUseChangeQuery,
} = landUseChangeApiSlice;

export const {
	useGetLandUseChangeQuery: usePublicGetLandUseChangeQuery,
} = publicLandUseChangeApiSlice;

export const {
	useUpdateLandUseChangeMutation,
} = mutationApiSlice;

// The by-activity endpoint may return either a bare array or a DRF-paginated
// { results: [...] } object (both shapes exist across this API — see
// getModuleTypes). It is empty when the activity has no land-use-change entry
// yet. Normalise to the first entry, or null when there is none, instead of
// parsing undefined (consumers optional-chain the result).
const transformResponseByActivityId = (
	response: LandUseChangeResponse[] | { results?: LandUseChangeResponse[] }
) => {
	const entries = Array.isArray(response) ? response : (response?.results ?? []);
	return entries.length ? transformResponse(entries[0]) : null;
}
const otherApis = createEntriesGetApi<ModuleFull>({
	apiSlice,
	path: ENDPOINT,
	moduleName: "LandUseChangeByActivityId",
	tag: TAG,
	transformResponse: transformResponseByActivityId,
})

const publicOtherApis = createEntriesGetApi<ModuleFull>({
	apiSlice: publicApiSlice,
	path: ENDPOINT,
	moduleName: "LandUseChangeByActivityId",
	tag: TAG,
	transformResponse: transformResponseByActivityId,
})


export const {
	useGetLandUseChangeByActivityIdQuery,
} = otherApis;

export const {
	useGetLandUseChangeByActivityIdQuery: usePublicGetLandUseChangeByActivityIdQuery,
} = publicOtherApis;

export const landUseChangeEndpoints = landUseChangeApiSlice.endpoints;
export const landUseChangeMutationEndpoints = mutationApiSlice.endpoints;
export const landUseChangeByActivityIdEndpoints = otherApis.endpoints;